// @flow

import * as React from 'react';

import classNames from '@ta/common.classnames';
import { Icon } from '@ta/styleguide.icon';

/* eslint css-modules/no-unused-class: [2, { markAsUsed: ['noPadding', 'narrow']}] */
import styles from './BasicResult.less';

/** Different styles of padding that can be applied to the result */
export type PaddingStyle =
  | 'noPadding' // No padding around the result (applies to active and inactive states)
  | 'narrow'; // Less top and bottom padding

/** The icon color */
export type IconStyle = 'green' | 'oldGreen' | 'gray';

/** Component props for a BasicResult */
export type Props = {|
  /**  The result title */
  title: string,

  /** The result subtitle */
  subtitle?: string,

  /** True if this result is active */
  active?: boolean,

  /** The optional <Icon/> name to display next to the title */
  iconName?: string,

  /** The optional <Icon/> name to display as a chevron */
  chevronName?: string,

  /** The optional verified icon to display after title */
  verifiedIcon?: boolean,

  /** Padding style for the result */
  paddingStyle?: PaddingStyle,

  /** True if this result is a rescue result */
  isRescue?: boolean,

  /** Optionally specify the icon color (green, or default gray) in the result. Default to null */
  iconStyle?: IconStyle,

  /** Optionally reverse the title and subtitle style, default to be false */
  reverseSubtitle?: boolean,
|};

/**
 * The visualization of a basic typeahead search result. It has a title, optional subtitle, active state, and optionally
 * a green icon on the left or optionally a black icon (typically a chevron) on the right.
 */
const BasicResult = (props: Props) => {
  const className = [styles.result];
  if (props.active) {
    className.push(styles.active);
  }
  if (props.paddingStyle) {
    className.push(styles[props.paddingStyle]);
  }
  if (props.isRescue) {
    className.push(styles.rescue);
  }
  const subElem = props.subtitle ? (
    <span className={props.reverseSubtitle ? styles.reversedSubtitle : styles.subtitle}>
      {props.subtitle}
    </span>
  ) : (
    undefined
  );
  const leftIcon = props.iconName ? (
    <span
      className={classNames(props.isRescue ? styles.rescueIcon : styles.leftIcon, {
        [styles.greenIcon]: props.iconStyle === 'green',
        [styles.oldGreenIcon]: props.iconStyle === 'oldGreen',
        [styles.grayIcon]: props.iconStyle === 'gray',
      })}
    >
      <Icon name={props.iconName} />
    </span>
  ) : (
    undefined
  );
  const rightIcon = props.chevronName ? (
    <span className={styles.rightIcon}>
      <Icon name={props.chevronName} />
    </span>
  ) : (
    undefined
  );
  const iconAfterTitle = props.verifiedIcon ? <span className={styles.verified} /> : undefined;
  return (
    <div className={className.join(' ')}>
      {leftIcon}
      <span className={classNames(styles.resultTitle, { [styles.rescueResult]: props.isRescue })}>
        {props.reverseSubtitle && subElem}
        {props.title}
        {iconAfterTitle}
        {!props.reverseSubtitle && subElem}
      </span>
      {rightIcon}
    </div>
  );
};

BasicResult.defaultProps = {
  subtitle: undefined,
  active: undefined,
  iconName: undefined,
  verifiedIcon: false,
  chevronName: undefined,
  paddingStyle: undefined,
  isRescue: false,
  iconStyle: 'oldGreen',
  reverseSubtitle: false,
};

export default BasicResult;
